import { useMemo } from 'react';

import { useAppSelector } from 'flow/hooks/useRedux';
import {
    selectHasNoSmsChatSessionWithFilter,
    selectColleaguesWithoutChat,
} from 'flow/selectors/chat-selectors';
import { getSearchText } from 'flow/selectors/selectors';
import { selectOwnUserHasCapability } from 'flow/selectors/ExtensionSelectors';
import { textFieldInputIsCallable } from 'flow/utils/call-utils';
import { Capabilities } from 'flow/utils/capability-utils';
import { ChatListRow } from './chat-list-rows';

/** The "start a new chat" section: colleagues there is no chat with yet, and an
 *  SMS row when what was typed is a number nobody holds. Only ever shown while
 *  searching, so only this half of the list reads any of it. */
const useNewChatRows = (): ChatListRow[] => {
    const searchText = useAppSelector(getSearchText);
    const extensionKeys = useAppSelector(selectColleaguesWithoutChat);
    const hasNoSmsChatSession = useAppSelector(
        selectHasNoSmsChatSessionWithFilter,
    );
    const ownUserHasCapabilitySms = useAppSelector((state) =>
        selectOwnUserHasCapability(state, Capabilities.SMS),
    );

    return useMemo<ChatListRow[]>(() => {
        const colleagueRows = extensionKeys
            .map(
                (extensionKey): ChatListRow => ({
                    kind: 'newChat',
                    extensionKey,
                }),
            )
            .valueSeq()
            .toArray();

        const smsIsOffered = !!(
            ownUserHasCapabilitySms &&
            searchText &&
            hasNoSmsChatSession &&
            textFieldInputIsCallable(searchText)
        );

        const smsRows: ChatListRow[] = smsIsOffered
            ? [{ kind: 'sms', searchText }]
            : [];

        if (!colleagueRows.length && !smsRows.length) {
            return [];
        }

        return [{ kind: 'newChatTitle' }, ...colleagueRows, ...smsRows];
    }, [
        searchText,
        extensionKeys,
        hasNoSmsChatSession,
        ownUserHasCapabilitySms,
    ]);
};

export default useNewChatRows;
